<template>
  <div class="jim-scheduling-container">

    <!-- FULL-WIDTH WRAPPER -->
    <div class="logistics-page">

      <h1 class="page-heading">
        Open Sales Orders
      </h1>

      <!-- MAIN LAYOUT: LEFT BUTTON/FILTER PANEL + RIGHT REPORT -->
      <div class="logistics-layout">

        <!-- =====================================================
             LEFT SIDE PANEL
             Global Location / Dashboard / Logout are in App.vue.
             DO NOT duplicate them here.
        ====================================================== -->
        <div class="left-side-section">

          <!-- Search -->
          <div class="control">
            <label>Search</label>

            <input
              v-model.trim="searchText"
              type="text"
              placeholder="Order, customer, item..."
            />
          </div>

          <!-- Status Filter -->
          <div class="control">
            <label>Status</label>

            <select v-model="selectedStatus">
              <option value="">All</option>

              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>

          <!-- Ship Via Filter -->
          <div class="control">
            <label>Ship Via</label>

            <select v-model="selectedShipVia">
              <option value="">All</option>

              <option
                v-for="shipVia in shipViaOptions"
                :key="shipVia"
                :value="shipVia"
              >
                {{ shipVia }}
              </option>
            </select>
          </div>

          <!-- Room for future dropdowns goes here -->

          <button
            class="plan-it-btn"
            @click="loadAll"
            :disabled="loading"
          >
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>

          <button
            class="plan-it-btn"
            @click="exportCsv"
            :disabled="loading || filteredRows.length === 0"
          >
            Export CSV
          </button>

          <button
            class="plan-it-btn"
            @click="clearFilters"
            :disabled="loading"
          >
            Clear Filters
          </button>

        </div>


        <!-- =====================================================
             RIGHT SIDE
        ====================================================== -->
        <div class="scheduler-wrapper">

          <!-- ===================================================
               SUMMARY BAR
          ==================================================== -->
          <div class="summary-bar">

            <div class="summary-item">
              <div class="summary-label">
                Open Orders
              </div>

              <div class="summary-value">
                {{ formatNum(orderCount) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Open Lines
              </div>

              <div class="summary-value">
                {{ formatNum(rows.length) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Showing
              </div>

              <div class="summary-value">
                {{ formatNum(filteredRows.length) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                Open Qty
              </div>

              <div class="summary-value">
                {{ formatNum(filteredOpenQty) }}
              </div>
            </div>

          </div>


          <!-- ===================================================
               ERROR
          ==================================================== -->
          <div
            v-if="errorMessage"
            class="error-message"
          >
            {{ errorMessage }}
          </div>


          <!-- ===================================================
               REPORT PANEL
          ==================================================== -->
          <div class="day-box">

            <div class="report-heading-row">

              <div>
                <div class="report-title">
                  Open Sales Order Detail
                </div>

                <div class="small-muted">
                  Location:
                  <strong>{{ selectedLocation }}</strong>

                  <span v-if="generatedAt">
                    &nbsp;•&nbsp;
                    Refreshed:
                    {{ formatTimestamp(generatedAt) }}
                  </span>
                </div>
              </div>

              <div class="small-muted">
                {{ filteredRows.length }}
                line{{ filteredRows.length === 1 ? '' : 's' }}
              </div>

            </div>


            <!-- LOADING -->
            <div
              v-if="loading"
              class="loading-message"
            >
              Loading open sales orders...
            </div>


            <!-- =================================================
                 SCROLLABLE TABLE
                 Vertical + horizontal scrolling.
                 Headers remain sticky.
            ================================================== -->
            <div
              v-else
              class="orders-scroll-container"
            >

              <table class="orders-table">

                <thead>
                  <tr>
                    <th>Sales Order</th>
                    <th>Entry Date</th>

                    <th>Customer #</th>
                    <th>Ship To Customer</th>
                    <th>City</th>
                    <th>ST</th>

                    <th>Customer PO</th>

                    <th>Status</th>
                    <th>Ship Via</th>
                    <th>Salesperson</th>

                    <th>Line #</th>

                    <th>Item</th>
                    <th>Description</th>

                    <th class="numeric-heading">
                      Qty Ordered
                    </th>

                    <th class="numeric-heading">
                      Qty Open
                    </th>

                    <th class="numeric-heading">
                      Backordered
                    </th>

                    <th>Req Ship</th>
                    <th>Request Date</th>
                    <th>Promise Date</th>
                    <th>Header Ship</th>
                  </tr>
                </thead>


                <tbody>

                  <tr
                    v-for="(row, index) in filteredRows"
                    :key="getRowKey(row, index)"
                    :class="{
                      'new-order-row': isNewOrder(index)
                    }"
                  >

                    <td class="order-number">
                      {{ row.ord_no }}
                    </td>

                    <td>
                      {{ formatDateOnly(row.entered_dt) }}
                    </td>

                    <td>
                      {{ row.cus_no }}
                    </td>

                    <td class="customer-name">
                      {{ row.ship_to_name }}
                    </td>

                    <td>
                      {{ row.ship_to_city }}
                    </td>

                    <td>
                      {{ row.ship_to_state }}
                    </td>

                    <td>
                      {{ row.customer_po }}
                    </td>

                    <td>
                      {{ row.ord_status }}
                    </td>

                    <td>
                      {{ row.ship_via_cd }}
                    </td>

                    <td>
                      {{ row.salesperson }}
                    </td>

                    <td>
                      {{ row.line_no }}
                    </td>

                    <td class="item-number">
                      {{ row.item_no }}
                    </td>

                    <td class="description-cell">
                      {{ buildDescription(row) }}
                    </td>

                    <td class="numeric-cell">
                      {{ formatNum(row.qty_ordered) }}
                    </td>

                    <td class="numeric-cell open-qty">
                      {{ formatNum(row.qty_to_ship) }}
                    </td>

                    <td class="numeric-cell">
                      {{ formatNum(row.qty_bkord) }}
                    </td>

                    <td>
                      {{ formatDateOnly(row.req_ship_dt) }}
                    </td>

                    <td>
                      {{ formatDateOnly(row.request_dt) }}
                    </td>

                    <td>
                      {{ formatDateOnly(row.promise_dt) }}
                    </td>

                    <td>
                      {{ formatDateOnly(row.header_ship_dt) }}
                    </td>

                  </tr>

                </tbody>

              </table>


              <div
                v-if="filteredRows.length === 0"
                class="empty-message"
              >
                No open sales-order lines found.
              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  </div>
</template>


<script>
import axios from 'axios';

export default {

  name: 'OpenSalesOrders',

  data() {
    return {

      rows: [],

      orderCount: 0,

      generatedAt: null,

      loading: false,

      errorMessage: '',

      searchText: '',

      selectedStatus: '',

      selectedShipVia: ''

    };
  },


  computed: {

    // Same exact global-location pattern used by LogisticsPage.
    selectedLocation() {
      return this.$root.selectedLocation;
    },


    statusOptions() {

      return [
        ...new Set(
          (this.rows || [])
            .map(row =>
              String(row.ord_status || '').trim()
            )
            .filter(Boolean)
        )
      ].sort();

    },


    shipViaOptions() {

      return [
        ...new Set(
          (this.rows || [])
            .map(row =>
              String(row.ship_via_cd || '').trim()
            )
            .filter(Boolean)
        )
      ].sort();

    },


    filteredRows() {

      const search =
        String(this.searchText || '')
          .trim()
          .toLowerCase();


      const status =
        String(this.selectedStatus || '')
          .trim()
          .toUpperCase();


      const shipVia =
        String(this.selectedShipVia || '')
          .trim()
          .toUpperCase();


      return (this.rows || []).filter(row => {

        // Status filter
        if (
          status &&
          String(row.ord_status || '')
            .trim()
            .toUpperCase() !== status
        ) {
          return false;
        }


        // Ship Via filter
        if (
          shipVia &&
          String(row.ship_via_cd || '')
            .trim()
            .toUpperCase() !== shipVia
        ) {
          return false;
        }


        // No search text = row passes.
        if (!search) {
          return true;
        }


        const searchable = [

          row.ord_no,

          row.cus_no,

          row.ship_to_name,

          row.ship_to_city,

          row.ship_to_state,

          row.customer_po,

          row.ord_status,

          row.ship_via_cd,

          row.salesperson,

          row.line_no,

          row.item_no,

          row.item_desc_1,

          row.item_desc_2,

          row.req_ship_dt

        ]
          .map(value =>
            String(value ?? '')
              .toLowerCase()
          )
          .join(' ');


        return searchable.includes(search);

      });

    },


    filteredOpenQty() {

      return (this.filteredRows || [])
        .reduce(
          (total, row) =>
            total + Number(row.qty_to_ship || 0),
          0
        );

    }

  },


  watch: {

    // Same pattern as LogisticsPage:
    // changing App.vue's global location reloads this page.
    selectedLocation() {

      this.clearUi();

      this.loadAll();

    }

  },


  methods: {

    // =========================================================
    // LOAD REPORT
    // =========================================================
    async loadAll() {

      if (!this.selectedLocation) {
        return;
      }


      const location =
        String(this.selectedLocation || '')
          .trim()
          .toUpperCase()
          .slice(0, 1);


      if (!['F', 'D', 'K', 'L'].includes(location)) {

        this.errorMessage =
          'Invalid location.';

        return;

      }


      this.loading = true;

      this.errorMessage = '';


      try {

        const { data } = await axios.get(
          '/getOpenSalesOrders',
          {
            params: {
              location
            }
          }
        );


        this.rows =
          Array.isArray(data?.rows)
            ? data.rows
            : [];


        this.orderCount =
          Number(data?.order_count || 0);


        this.generatedAt =
          data?.generated_at || null;


        console.log(
          '✅ Open sales orders loaded:',
          {
            location,
            orderCount: this.orderCount,
            lines: this.rows.length
          }
        );


      } catch (err) {

        console.error(
          'loadAll open orders error:',
          err.response?.data || err
        );


        this.rows = [];

        this.orderCount = 0;

        this.generatedAt = null;


        this.errorMessage =
          err.response?.data?.error ||
          'Failed to load open sales orders.';

      } finally {

        this.loading = false;

      }

    },


    // =========================================================
    // CLEAR DATA WHEN LOCATION CHANGES
    // =========================================================
    clearUi() {

      this.rows = [];

      this.orderCount = 0;

      this.generatedAt = null;

      this.errorMessage = '';

    },


    // =========================================================
    // CLEAR FILTERS
    // =========================================================
    clearFilters() {

      this.searchText = '';

      this.selectedStatus = '';

      this.selectedShipVia = '';

    },


    // =========================================================
    // UNIQUE ROW KEY
    // =========================================================
    getRowKey(row, index) {

      return [
        row?.ord_no || '',
        row?.line_seq_no || '',
        row?.line_no || '',
        row?.item_no || '',
        index
      ].join('-');

    },


    // =========================================================
    // VISUAL SEPARATOR BETWEEN SALES ORDERS
    // =========================================================
    isNewOrder(index) {

      if (index === 0) {
        return true;
      }


      const current =
        this.filteredRows[index];


      const previous =
        this.filteredRows[index - 1];


      return (
        String(current?.ord_no || '') !==
        String(previous?.ord_no || '')
      );

    },


    // =========================================================
    // DESCRIPTION
    // =========================================================
    buildDescription(row) {

      return [
        row?.item_desc_1,
        row?.item_desc_2
      ]
        .map(value =>
          String(value || '').trim()
        )
        .filter(Boolean)
        .join(' ');

    },


    // =========================================================
    // NUMBER FORMAT
    // =========================================================
    formatNum(n) {

      if (
        n === null ||
        n === undefined ||
        n === ''
      ) {
        return '';
      }


      const x = Number(n);


      if (Number.isNaN(x)) {
        return n;
      }


      return x.toLocaleString(
        undefined,
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }
      );

    },


    // =========================================================
    // DATE FORMAT
    //
    // Macola DATE values often arrive as timestamps.
    // Take YYYY-MM-DD without timezone shifting.
    // =========================================================
    formatDateOnly(value) {

      if (!value) {
        return '';
      }


      const text =
        String(value).trim();


      const match =
        text.match(
          /^(\d{4})-(\d{2})-(\d{2})/
        );


      if (match) {

        return (
          `${match[2]}/` +
          `${match[3]}/` +
          `${match[1]}`
        );

      }


      return text;

    },


    // =========================================================
    // REFRESH TIMESTAMP
    // =========================================================
    formatTimestamp(value) {

      if (!value) {
        return '';
      }


      const date =
        new Date(value);


      if (
        Number.isNaN(date.getTime())
      ) {
        return '';
      }


      return date.toLocaleString();

    },


    // =========================================================
    // CSV VALUE ESCAPE
    // =========================================================
    csvEscape(value) {

      const text =
        String(value ?? '');


      return (
        '"' +
        text.replace(/"/g, '""') +
        '"'
      );

    },


    // =========================================================
    // EXPORT CURRENT FILTERED VIEW
    // =========================================================
    exportCsv() {

      if (!this.filteredRows.length) {
        return;
      }


      const columns = [

        ['Sales Order', 'ord_no'],

        ['Order Entry Date', 'entered_dt'],

        ['Customer Number', 'cus_no'],

        ['Ship To Customer', 'ship_to_name'],

        ['Ship To City', 'ship_to_city'],

        ['Ship To State', 'ship_to_state'],

        ['Customer PO', 'customer_po'],

        ['Order Status', 'ord_status'],

        ['Ship Via', 'ship_via_cd'],

        ['Salesperson', 'salesperson'],

        ['Line Number', 'line_no'],

        ['Line Sequence', 'line_seq_no'],

        ['Item Number', 'item_no'],

        ['Description 1', 'item_desc_1'],

        ['Description 2', 'item_desc_2'],

        ['Qty Ordered', 'qty_ordered'],

        ['Qty Open', 'qty_to_ship'],

        ['Qty Backordered', 'qty_bkord'],

        ['Requested Ship Date', 'req_ship_dt'],

        ['Request Date', 'request_dt'],

        ['Promise Date', 'promise_dt'],

        ['Header Ship Date', 'header_ship_dt']

      ];


      const header =
        columns
          .map(([label]) =>
            this.csvEscape(label)
          )
          .join(',');


      const body =
        this.filteredRows
          .map(row =>
            columns
              .map(([, field]) =>
                this.csvEscape(
                  row[field]
                )
              )
              .join(',')
          )
          .join('\r\n');


      const csv =
        '\uFEFF' +
        header +
        '\r\n' +
        body;


      const blob =
        new Blob(
          [csv],
          {
            type:
              'text/csv;charset=utf-8;'
          }
        );


      const url =
        URL.createObjectURL(blob);


      const link =
        document.createElement('a');


      const today =
        new Date()
          .toISOString()
          .slice(0, 10);


      const loc =
        String(
          this.selectedLocation || ''
        )
          .trim()
          .toUpperCase();


      link.href = url;


      link.download =
        `Open_Sales_Orders_${loc}_${today}.csv`;


      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

    }

  },


  mounted() {

    this.loadAll();

  }

};
</script>


<style scoped>

/* ===========================================================
   SAME BASE LOOK AS LOGISTICS
=========================================================== */

.jim-scheduling-container {

  font-family: Garamond, serif;

  -webkit-font-smoothing: antialiased;

  -moz-osx-font-smoothing: grayscale;

  text-align: center;

  color: #cbbfbf;

  margin-top: 20px;

  display: flex;

  flex-direction: column;

  align-items: stretch;

  background-color: #0b0101;

  min-height: 100vh;

  padding-bottom: 40px;

}


/* ===========================================================
   PAGE HEADING
=========================================================== */

.page-heading {

  font-family: 'Cormorant Garamond', serif;

  font-size: 36px;

  color: #cbbfbf;

  margin-bottom: 20px;

  text-align: center;

}


/* ===========================================================
   FULL-WIDTH WRAPPER
=========================================================== */

.logistics-page {

  width: 100%;

  max-width: 100%;

  box-sizing: border-box;

}


/* ===========================================================
   SAME LEFT/RIGHT GRID AS LOGISTICS
=========================================================== */

.logistics-layout {

  display: grid;

  grid-template-columns: 200px 1fr;

  gap: 12px;

  width: 100%;

  padding: 0 14px;

  box-sizing: border-box;

  align-items: start;

}


/* ===========================================================
   LEFT FILTER / BUTTON PANEL
=========================================================== */

.left-side-section {

  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 10px;

  margin-right: 10px;

  color: #fff;

  margin-top: 80px;

}


/* ===========================================================
   RIGHT REPORT AREA
=========================================================== */

.scheduler-wrapper {

  width: 100%;

  min-width: 0;

}


/* ===========================================================
   CONTROLS
=========================================================== */

.control {

  display: flex;

  flex-direction: column;

  gap: 6px;

  text-align: left;

  width: 100%;

}


.control label {

  font-size: 13px;

  color: #cbbfbf;

  font-weight: 600;

}


.control select,
.control input {

  padding: 8px 10px;

  border-radius: 8px;

  border: 1px solid #444;

  background-color: #111;

  color: #cbbfbf;

  min-width: 180px;

  font-family: inherit;

  width: 100%;

  box-sizing: border-box;

}


/* ===========================================================
   SAME PABLO BUTTON STYLE
=========================================================== */

.plan-it-btn {

  border-radius: 10px;

  padding: 8px 16px;

  width: 125px;

  background-color: #f3f4f6;

  border: 1px solid #cfd3d8;

  color: #333;

  font-weight: 600;

  cursor: pointer;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  font-family: inherit;

}


.plan-it-btn:hover:not(:disabled) {

  background-color: #e5e7eb;

  border-color: #bfc5cc;

}


.plan-it-btn:disabled {

  background-color: #e5e7eb;

  color: #888;

  cursor: not-allowed;

}


/* ===========================================================
   SAME SUMMARY BAR STYLE AS LOGISTICS
=========================================================== */

.summary-bar {

  display: flex;

  justify-content: space-between;

  gap: 18px;

  padding: 10px 12px;

  border-radius: 10px;

  border: 1px solid #888;

  margin: 0 0 12px;

  background: rgb(67, 67, 67);

  flex-wrap: wrap;

  width: 100%;

  box-sizing: border-box;

}


.summary-item {

  min-width: 140px;

  flex: 1 1 140px;

  display: flex;

  flex-direction: column;

  align-items: center;

  text-align: center;

}


.summary-label {

  font-size: 18px;

  opacity: 0.8;

  line-height: 1.1;

}


.summary-value {

  font-size: 24px;

  font-weight: bold;

  color: #cbbfbf;

  line-height: 1.1;

  margin-top: 2px;

}


/* ===========================================================
   SAME "DAY BOX" PANEL STYLE
=========================================================== */

.day-box {

  background-color: #0b0101;

  border: 1px solid #888;

  border-radius: 10px;

  padding: 12px;

  text-align: left;

  min-width: 0;

}


/* ===========================================================
   REPORT HEADER
=========================================================== */

.report-heading-row {

  display: flex;

  justify-content: space-between;

  align-items: center;

  gap: 12px;

  margin-bottom: 10px;

}


.report-title {

  font-family:
    'Cormorant Garamond',
    Garamond,
    serif;

  font-size: 20px;

  font-weight: bold;

  color: #cbbfbf;

}


.small-muted {

  font-size: 12px;

  opacity: 0.75;

  color: #cbbfbf;

}


/* ===========================================================
   TABLE SCROLLING

   Vertical scroll:
   max-height / overflow-y

   Horizontal scroll:
   overflow-x

   This is the element the sticky header sticks within.
=========================================================== */

.orders-scroll-container {

  width: 100%;

  max-height: 72vh;

  overflow-x: auto;

  overflow-y: auto;

  position: relative;

  border-radius: 8px;

  min-width: 0;

}


/* ===========================================================
   TABLE

   min-width intentionally wider than viewport so horizontal
   scrolling works rather than crushing 20 columns together.
=========================================================== */

.orders-table {

  width: 100%;

  min-width: 2200px;

  border-collapse: separate;

  border-spacing: 0;

  color: #cbbfbf;

  font-family: Garamond, serif;

  font-size: 13px;

}


/* ===========================================================
   STICKY TABLE HEADER
=========================================================== */

.orders-table thead th {

  position: sticky;

  top: 0;

  z-index: 20;

  background: #111827;

  color: #cbbfbf;

  font-weight: bold;

  text-align: left;

  padding: 8px;

  border-right: 1px solid #333;

  border-bottom: 1px solid #555;

  white-space: nowrap;

}


/* ===========================================================
   CELLS
=========================================================== */

.orders-table tbody td {

  padding: 7px 8px;

  border-right: 1px solid #333;

  border-bottom: 1px solid #333;

  white-space: nowrap;

  background: #0b0101;

  vertical-align: top;

}


/* ===========================================================
   ALTERNATING ROWS
=========================================================== */

.orders-table tbody tr:nth-child(even) td {

  background: #0f141c;

}


/* ===========================================================
   HOVER
=========================================================== */

.orders-table tbody tr:hover td {

  background: #172233;

}


/* ===========================================================
   SEPARATOR BETWEEN SALES ORDERS
=========================================================== */

.orders-table tbody tr.new-order-row td {

  border-top: 2px solid #777;

}


/* ===========================================================
   COLUMN FORMATTING
=========================================================== */

.order-number {

  font-weight: bold;

}


.item-number {

  font-weight: bold;

}


.customer-name {

  min-width: 180px;

}


.description-cell {

  min-width: 240px;

  max-width: 340px;

  white-space: normal !important;

  line-height: 1.15;

}


.numeric-heading {

  text-align: right !important;

}


.numeric-cell {

  text-align: right;

  font-variant-numeric: tabular-nums;

}


.open-qty {

  font-weight: bold;

}


/* ===========================================================
   ERROR
=========================================================== */

.error-message {

  margin-bottom: 12px;

  padding: 10px 12px;

  border-radius: 10px;

  border: 1px solid #aa4444;

  background: #2a0b0b;

  color: #fca5a5;

}


/* ===========================================================
   LOADING / EMPTY
=========================================================== */

.loading-message,
.empty-message {

  padding: 25px;

  text-align: center;

  color: #cbbfbf;

}

</style>